import type { ReactNode } from 'react';

import { usePlatform } from '../platform/index.ts';

interface PinKeypadProps {
  // Receives the literal digit ('0'..'9') or the sentinel 'del' for
  // backspace. The caller owns the value buffer; this component is
  // a pure dispatcher.
  readonly onKey: (key: KeypadKey) => void;
  readonly disabled: boolean;
}

type KeypadKey = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | 'del';

// Bottom row leaves a spacer on the left so 0 lines up under 8 — the
// layout every native iOS / Android numeric keypad uses. A two-key
// bottom row would put 0 where users expect 7, increasing misclicks
// adjacent to the delete key.
const ROWS: readonly (readonly (KeypadKey | 'spacer')[])[] = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['spacer', '0', 'del'],
];

// Custom numeric keypad for PIN + activation entry. We deliberately
// do not rely on the native numeric keyboard because the Telegram iOS
// WebView inconsistently honours `inputMode="numeric"` and the soft-
// keyboard shift pushes our success panel off-viewport. The keypad
// also lets us hook haptic feedback per keystroke, which the native
// keyboard does not expose.
export const PinKeypad = (props: PinKeypadProps): ReactNode => {
  const platform = usePlatform();
  const { onKey, disabled } = props;
  return (
    <div className="pin-keypad" role="group" aria-label="Numeric keypad">
      {ROWS.map((row, rIdx) => (
        <div key={rIdx} className="pin-keypad-row">
          {row.map((k, kIdx) => {
            if (k === 'spacer') {
              return <span key={`spacer-${String(kIdx)}`} className="pin-keypad-spacer" aria-hidden="true" />;
            }
            const isDel = k === 'del';
            return (
              <button
                key={k}
                type="button"
                className={`pin-keypad-key${isDel ? ' del' : ''}`}
                onClick={(): void => {
                  platform.haptic('light');
                  onKey(k);
                }}
                disabled={disabled}
                aria-label={isDel ? 'Backspace' : k}
              >
                {isDel ? '⌫' : k}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};
